import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiSearch, FiEye, FiRefreshCw, FiTrendingUp, FiUsers, FiPlus, FiCopy, FiTrash2, FiKey } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import PSODetailsModal from '../components/PSODetailsModal';
import DeactivationModal from '../components/DeactivationModal';
import AddStaffModal from '../components/AddStaffModal';
import Modal from '../../../components/Modal';
import { fetchPSOs, updatePSO, deletePSOStaffFirestoreRecord, unassignPsoFromAllBranches, type PSO } from '../service/psoService';
import { fetchBranchBankId } from '../../branches/service/branchesService';
import Table, { Column } from '../../../components/Table';
import { updateUserEmail, deleteAuthUser } from '../../../services/passwordService';
import { useAppSelector, useAppDispatch } from '../../../store';
import { setUser, setPso } from '../../../store/slices';
import { isPsoHeadDesignation } from '../../../utils/psoDesignation';
import { designationIsIncharge } from '../../../utils/designationRole';
import { getPostLoginHomePathForPso } from '../../../navigation/postLoginHomePath';
import { useNavigate } from 'react-router-dom';

export default function PSO() {
    const [psos, setPsos] = useState<PSO[]>([]);
    const [filtered, setFiltered] = useState<PSO[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedPSO, setSelectedPSO] = useState<PSO | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [psoToDeactivate, setPsoToDeactivate] = useState<PSO | null>(null);
    const [loading, setLoading] = useState(true);
    const [bankId, setBankId] = useState<string | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [authFilter, setAuthFilter] = useState<'all' | 'withAuth' | 'noAuth'>('all');
    /** Bank only: filter rows by active / inactive. PSO users never see inactive staff. */
    const [staffStatusFilter, setStaffStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [designations, setDesignations] = useState<string[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [psoToDelete, setPsoToDelete] = useState<PSO | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentBank = useAppSelector((s) => s.user.bank);
    const [showAccessConfirmModal, setShowAccessConfirmModal] = useState(false);
    const [psoToAccess, setPsoToAccess] = useState<PSO | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const psoProfile = useAppSelector((s) => s.user.pso);
    const isHeadReadOnly = !isBankUser && isPsoHeadDesignation(psoProfile?.designation);

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                let fetchedBankId: string | null = null;

                if (isBankUser) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const { getDoc, doc } = await import('firebase/firestore');
                    const { db } = await import('../../../firebase/config');
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        fetchedBankId = uid;
                    } else {
                        // Try query by userId field
                        const { getDocs, collection } = await import('firebase/firestore');
                        const banksSnap = await getDocs(collection(db, "banks"));
                        for (const bankDoc of banksSnap.docs) {
                            const bankData = bankDoc.data();
                            if (bankData.userId === uid) {
                                fetchedBankId = bankDoc.id;
                                break;
                            }
                        }
                    }
                } else {
                    // For PSO users, find the bankId
                    fetchedBankId = await fetchBranchBankId(uid);
                }

                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const psosData = await fetchPSOs(fetchedBankId);
                    setPsos(psosData);

                    // Extract unique designations
                    const designationSet = new Set<string>();
                    psosData.forEach(pso => {
                        if (pso.designation) {
                            designationSet.add(pso.designation);
                        }
                    });
                    setDesignations(Array.from(designationSet));

                    // Filtered list will be set by useEffect
                }
            } catch (err: any) {
                showToast('Failed to load PSOs data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    /** KPI cards: bank sees everyone; PSO / non-bank sees active staff only (inactive is bank-only). */
    const kpiPsos = useMemo(
        () => (isBankUser ? psos : psos.filter((p) => p.isActive !== false)),
        [isBankUser, psos]
    );

    const kpiPool = useMemo(() => {
        // When a designation filter is selected from the dropdown, calculate KPI cards from the same pool.
        // (Search term is intentionally NOT applied here.)
        let list = kpiPsos;
        if (selectedFilter && selectedFilter !== 'All') {
            const isIncharge = (d?: string) => {
                const norm = (d || '').toLowerCase();
                return norm.includes('incharge') || norm.includes('inchage');
            };
            if (selectedFilter === 'Head') {
                list = list.filter(
                    (pso) =>
                        pso.designation?.toLowerCase().includes('head') &&
                        !pso.designation?.toLowerCase().includes('regional')
                );
            } else if (selectedFilter === 'Regional Head') {
                list = list.filter((pso) => pso.designation?.toLowerCase().includes('regional'));
            } else if (selectedFilter === 'PSO') {
                // PSO means PSO staff only (exclude incharges).
                list = list.filter((pso) => {
                    const d = (pso.designation || '').toLowerCase();
                    if (!d) return true; // legacy docs default to PSO
                    if (isIncharge(d)) return false;
                    if (d === 'pso') return true;
                    return !d.includes('head') && !d.includes('regional');
                });
            } else {
                list = list.filter((pso) => pso.designation === selectedFilter);
            }
        }
        return list;
    }, [kpiPsos, selectedFilter]);

    useEffect(() => {
        let list = psos;

        if (!isBankUser) {
            list = list.filter((p) => p.isActive !== false);
        } else {
            if (staffStatusFilter === 'active') {
                list = list.filter((p) => p.isActive !== false);
            } else if (staffStatusFilter === 'inactive') {
                list = list.filter((p) => p.isActive === false);
            }
        }

        // Filter by Auth registration status
        if (authFilter === 'withAuth') {
            list = list.filter((pso) => Boolean(pso.authUid));
        } else if (authFilter === 'noAuth') {
            list = list.filter((pso) => !pso.authUid);
        }

        // Apply filter based on selected designation
        if (selectedFilter && selectedFilter !== 'All') {
            if (selectedFilter === 'Head') {
                list = list.filter(pso =>
                    pso.designation?.toLowerCase().includes('head') &&
                    !pso.designation?.toLowerCase().includes('regional')
                );
            } else if (selectedFilter === 'Regional Head') {
                list = list.filter(pso => pso.designation?.toLowerCase().includes('regional'));
            } else if (selectedFilter === 'PSO') {
                list = list.filter(pso =>
                    !pso.designation ||
                    pso.designation?.toLowerCase() === 'pso' ||
                    (
                        !pso.designation?.toLowerCase().includes('head') &&
                        !pso.designation?.toLowerCase().includes('regional') &&
                        !pso.designation?.toLowerCase().includes('incharge') &&
                        !pso.designation?.toLowerCase().includes('inchage')
                    )
                );
            } else {
                list = list.filter(pso => pso.designation === selectedFilter);
            }
        }

        // Apply search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (pso) =>
                    pso.name?.toLowerCase().includes(term) ||
                    pso.contact?.toLowerCase().includes(term) ||
                    pso.email?.toLowerCase().includes(term)
            );
        }

        // Sort by name alphabetically
        list.sort((a, b) => {
            const nameA = (a.name || '').toString();
            const nameB = (b.name || '').toString();
            return nameA.localeCompare(nameB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, psos, selectedFilter, authFilter, isBankUser, staffStatusFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setAuthFilter('all');
        setStaffStatusFilter('all');
        setFiltered(psos);
    };

    const handleStatCardClick = (designation: string) => {
        setSelectedFilter(selectedFilter === designation ? null : designation);
    };

    const openDetailsModal = (pso: PSO) => {
        setSelectedPSO(pso);
        setShowDetailsModal(true);
    };

    const handleStatusToggle = (pso: PSO) => {
        if (!isBankUser) return;
        // If PSO is active, show deactivation modal
        if (pso.isActive !== false) {
            setPsoToDeactivate(pso);
            setShowDeactivationModal(true);
        } else {
            // If PSO is inactive, reactivate directly
            handleReactivate(pso);
        }
    };

    const handleAccessClick = (pso: PSO) => {
        if (!pso.authUid) {
            showToast('This staff member has no login credentials (no Auth UID).', 'warning');
            return;
        }
        setPsoToAccess(pso);
        setShowAccessConfirmModal(true);
    };

    const handleAccessConfirm = () => {
        if (!psoToAccess || !bankId) return;

        try {
            sessionStorage.setItem('impersonatedUser', JSON.stringify(psoToAccess));
            if (currentBank) {
                sessionStorage.setItem('impersonatedBank', JSON.stringify(currentBank));
            }
            sessionStorage.setItem('bankDocId', bankId);
            sessionStorage.setItem('psoDocId', psoToAccess.id);

            const isIncharge = designationIsIncharge(psoToAccess.designation);
            sessionStorage.setItem('role', isIncharge ? 'incharge' : 'pso');
            if (psoToAccess.designation) {
                sessionStorage.setItem('psoDesignation', psoToAccess.designation);
            } else {
                sessionStorage.removeItem('psoDesignation');
            }

            dispatch(setUser({
                uid: psoToAccess.authUid || psoToAccess.id,
                email: psoToAccess.email || '',
                displayName: psoToAccess.name || '',
                photoURL: null,
            }));

            dispatch(setPso({
                authUid: psoToAccess.authUid || '',
                name: psoToAccess.name || '',
                email: psoToAccess.email || '',
                contact: psoToAccess.contact || '',
                designation: psoToAccess.designation || '',
                createdAt: psoToAccess.createdAt || '',
            }));

            showToast('Logged in as ' + psoToAccess.name + ' successfully!', 'success');
            setShowAccessConfirmModal(false);

            const nextPath = getPostLoginHomePathForPso(psoToAccess);
            navigate(nextPath, { replace: true });
        } catch (error: any) {
            console.error('Error switching user session:', error);
            showToast('Failed to switch user context', 'error');
        }
    };

    const isCurrentUserStaffRecord = (pso: PSO) => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return false;
        return pso.authUid === uidNow || pso.id === uidNow;
    };

    const handleDeleteClick = (pso: PSO) => {
        if (!isBankUser || !bankId) return;
        if (isCurrentUserStaffRecord(pso)) {
            showToast('You cannot delete your own account from this list.', 'warning');
            return;
        }
        setPsoToDelete(pso);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!isBankUser || !bankId || !psoToDelete) return;
        if (isCurrentUserStaffRecord(psoToDelete)) {
            showToast('You cannot delete your own account.', 'warning');
            return;
        }

        setDeleteSubmitting(true);
        try {
            // 1) Unassign this PSO from all branches first
            const idsToMatch = [psoToDelete.id, (psoToDelete as any).psoId].filter(Boolean) as string[];
            await unassignPsoFromAllBranches(bankId, idsToMatch);

            // 2) Remove Auth account (when exists)
            const authUid = psoToDelete.authUid?.trim();
            if (authUid) {
                await deleteAuthUser(authUid);
            }
            // 3) Remove Firestore staff record
            await deletePSOStaffFirestoreRecord(bankId, psoToDelete.id);

            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);
            const designationSet = new Set<string>();
            psosData.forEach((p) => {
                if (p.designation) designationSet.add(p.designation);
            });
            setDesignations(Array.from(designationSet));

            setShowDeleteModal(false);
            setPsoToDelete(null);
            showToast('✅ Staff member removed.', 'success');
        } catch (err: any) {
            console.error('Error deleting PSO:', err);
            showToast(err?.message || '❌ Failed to delete staff', 'error');
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const handleDeactivate = async (reason: string, date: string) => {
        if (!psoToDeactivate || !bankId) return;

        setLoading(true);
        try {
            const psoRef = doc(db, 'banks', bankId, 'pos', psoToDeactivate.id);
            await updateDoc(psoRef, {
                isActive: false,
                deactivationReason: reason,
                deactivationDate: date,
                updatedAt: new Date()
            });

            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            setShowDeactivationModal(false);
            setPsoToDeactivate(null);
            showToast('✅ PSO deactivated successfully!', 'success');
        } catch (err: any) {
            console.error('Error deactivating PSO:', err);
            showToast('❌ Failed to deactivate PSO', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async (pso: PSO) => {
        if (!bankId) return;

        setLoading(true);
        try {
            const psoRef = doc(db, 'banks', bankId, 'pos', pso.id);
            await updateDoc(psoRef, {
                isActive: true,
                deactivationReason: null,
                deactivationDate: null,
                updatedAt: new Date()
            });

            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            showToast('✅ PSO reactivated successfully!', 'success');
        } catch (err: any) {
            console.error('Error reactivating PSO:', err);
            showToast('❌ Failed to reactivate PSO', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStaffAdded = async () => {
        if (!bankId) return;

        setLoading(true);
        try {
            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            setShowAddModal(false);
            showToast('✅ Staff added successfully!', 'success');
        } catch (err: any) {
            console.error('Error refreshing PSOs:', err);
            showToast('❌ Failed to refresh staff list', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && psos.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading PSOs...</p>
                </div>
            </div>
        );
    }


    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const copyToClipboard = async (value: string) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            showToast('✅ Copied!', 'success');
        } catch {
            try {
                const el = document.createElement('textarea');
                el.value = value;
                el.style.position = 'fixed';
                el.style.left = '-9999px';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                showToast('✅ Copied!', 'success');
            } catch {
                showToast('❌ Copy failed', 'error');
            }
        }
    };

    const isDev = process.env.NODE_ENV === 'development';

    const columns: Column<PSO>[] = [
        {
            header: 'Name & Email',
            width: '32%',
            render: (pso) => (
                <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{pso.name || 'N/A'}</span>
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{pso.email || '—'}</span>
                        {pso.email && (
                            <button
                                type="button"
                                onClick={() => copyToClipboard(pso.email as string)}
                                className="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                                aria-label="Copy email"
                                title="Copy email"
                            >
                                <Icon icon={FiCopy} className="h-3 w-3" />
                            </button>
                        )}
                    </div>
                </div>
            )
        },
        { header: 'Contact', accessor: 'contact', width: '20%', className: 'text-gray-600 dark:text-gray-400' },
        {
            header: 'Auth UID',
            width: '18%',
            render: (pso) => (
                <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                        {pso.authUid ? pso.authUid : '—'}
                    </span>
                    {pso.authUid && (
                        <button
                            type="button"
                            onClick={() => copyToClipboard(pso.authUid as string)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                            aria-label="Copy auth uid"
                            title="Copy auth uid"
                        >
                            <Icon icon={FiCopy} className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            )
        },
        {
            header: 'Designation',
            accessor: 'designation',
            width: '15%',
            render: (pso) => pso.designation ? (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {pso.designation}
                </span>
            ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
            )
        },
        {
            header: 'Status',
            width: '10%',
            align: 'center',
            render: (pso) => isBankUser ? (
                <div className="flex flex-col gap-2 items-center">
                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={pso.isActive !== false}
                            onChange={() => handleStatusToggle(pso)}
                            className="peer sr-only"
                            disabled={loading || !bankId} // Disable if loading or no bankId
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 sr-only">
                            {pso.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                    </label>
                    {pso.isActive === false && pso.deactivationReason && (
                        <div className="mt-1 w-full">
                            <div
                                className="cursor-help text-justify"
                                title={`Reason: ${pso.deactivationReason}${pso.deactivationDate ? `\nDate: ${new Date(pso.deactivationDate).toLocaleDateString()}` : ''}`}
                            >
                                <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
                                    <strong>Reason:</strong> {pso.deactivationReason}
                                </p>
                                {pso.deactivationDate && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        <strong>Date:</strong> {new Date(pso.deactivationDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2 items-center">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${pso.isActive !== false ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {pso.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                    {pso.isActive === false && pso.deactivationReason && (
                        <div className="mt-1 w-full">
                            <div
                                className="cursor-help text-justify"
                                title={`Reason: ${pso.deactivationReason}${pso.deactivationDate ? `\nDate: ${new Date(pso.deactivationDate).toLocaleDateString()}` : ''}`}
                            >
                                <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
                                    <strong>Reason:</strong> {pso.deactivationReason}
                                </p>
                                {pso.deactivationDate && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        <strong>Date:</strong> {new Date(pso.deactivationDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )
        },
        {
            header: 'Action',
            width: isBankUser ? (isDev ? '18%' : '14%') : '10%',
            align: 'center',
            render: (pso) => (
                <div className="inline-flex flex-wrap items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={() => openDetailsModal(pso)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                        aria-label="View PSO details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isBankUser && (
                        <>
                            {isDev && (
                                <button
                                    type="button"
                                    onClick={() => handleAccessClick(pso)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                                    aria-label="Access user account"
                                    title="Access user account (Impersonate)"
                                >
                                    <Icon icon={FiKey} className="h-4 w-4" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => handleDeleteClick(pso)}
                                disabled={!bankId}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition-all hover:border-red-400 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 dark:border-red-900/50 dark:bg-gray-800 dark:text-red-400 dark:hover:border-red-500 dark:hover:bg-red-950/30"
                                aria-label="Delete staff"
                                title="Delete staff (Firestore + Firebase Auth)"
                            >
                                <Icon icon={FiTrash2} className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>
            )
        }
    ];

    const headerActions = isBankUser ? (
        <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2"
        >
            <Icon icon={FiPlus} className="h-4 w-4" />
            Add Staff
        </Button>
    ) : null;

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by name, contact, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            {(searchTerm || selectedFilter || authFilter !== 'all' || staffStatusFilter !== 'all') && (
                <Button
                    variant="secondary"
                    onClick={handleReset}
                    className="flex items-center gap-2"
                >
                    <Icon icon={FiRefreshCw} className="h-4 w-4" />
                    Reset
                </Button>
            )}

            <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    Auth
                </label>
                <select
                    value={authFilter}
                    onChange={(e) => setAuthFilter(e.target.value as any)}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="all">All</option>
                    <option value="withAuth">With UID</option>
                    <option value="noAuth">No UID</option>
                </select>
            </div>

            <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    Designation
                </label>
                <select
                    value={selectedFilter ?? ''}
                    onChange={(e) => setSelectedFilter(e.target.value ? e.target.value : null)}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="">All</option>
                    <option value="PSO">PSO</option>
                    <option value="Head">Head</option>
                    <option value="Regional Head">Regional Head</option>
                    {designations
                        .filter((d) => d && !['PSO', 'Head', 'Regional Head'].includes(d))
                        .map((d) => (
                            <option key={d} value={d}>
                                {String(d).replace(/_/g, ' ')}
                            </option>
                        ))}
                </select>
            </div>

            {isBankUser && (
                <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        Status
                    </label>
                    <select
                        value={staffStatusFilter}
                        onChange={(e) => setStaffStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                        className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                    >
                        <option value="all">All</option>
                        <option value="active">Active only</option>
                        <option value="inactive">Inactive only</option>
                    </select>
                </div>
            )}
        </div>
    );

    return (
        <>
            <div className="space-y-6">
                {isHeadReadOnly && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                        Read-only access for Head: view staff and details only. Add, status changes, and delete are available to bank users only.
                        Inactive accounts are hidden; counts show active staff only.
                    </p>
                )}
                {/* Statistics Cards - Designations */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Total Staff Card (Non-clickable) */}
                    {(() => {
                        const pool = kpiPool;
                        const activeCount = pool.filter((p) => p.isActive !== false).length;
                        const inactiveCount = pool.filter((p) => p.isActive === false).length;
                        return (
                            <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Staff</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{pool.length}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            {isBankUser ? (
                                                <>Active: {activeCount} | Inactive: {inactiveCount}</>
                                            ) : (
                                                <>Active accounts only (inactive hidden)</>
                                            )}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiUsers} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full w-full bg-gray-500 dark:bg-gray-500"
                                        title={
                                            isBankUser
                                                ? `Total: ${pool.length} (Active: ${activeCount}, Inactive: ${inactiveCount})`
                                                : `Active staff: ${pool.length}`
                                        }
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Head Card */}
                    {(() => {
                        const headPSOs = kpiPool.filter(p => p.designation?.toLowerCase().includes('head') && !p.designation?.toLowerCase().includes('regional'));
                        const count = headPSOs.length;
                        const activeCount = headPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = headPSOs.filter(p => p.isActive === false).length;
                        const percentage = kpiPool.length > 0 ? (count / kpiPool.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('Head')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'Head'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Head</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Regional Head Card */}
                    {(() => {
                        const regionalPSOs = kpiPool.filter(p => p.designation?.toLowerCase().includes('regional'));
                        const count = regionalPSOs.length;
                        const activeCount = regionalPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = regionalPSOs.filter(p => p.isActive === false).length;
                        const percentage = kpiPool.length > 0 ? (count / kpiPool.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('Regional Head')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'Regional Head'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Head</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* PSO Card */}
                    {(() => {
                        const psoPSOs = kpiPool.filter(p => !p.designation || p.designation?.toLowerCase() === 'pso' || (!p.designation?.toLowerCase().includes('head') && !p.designation?.toLowerCase().includes('regional') && !p.designation?.toLowerCase().includes('incharge') && !p.designation?.toLowerCase().includes('inchage')));
                        const count = psoPSOs.length;
                        const activeCount = psoPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = psoPSOs.filter(p => p.isActive === false).length;
                        const percentage = kpiPool.length > 0 ? (count / kpiPool.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('PSO')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'PSO'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">PSO</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                <Table
                    title={`Staff (${filtered.length})`}
                    description="Manage and view all PSO information"
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(pso) => pso.id}
                    loading={loading}
                    emptyMessage={
                        searchTerm || selectedFilter || authFilter !== 'all' || staffStatusFilter !== 'all'
                            ? 'No staff match these filters'
                            : 'No PSOs available'
                    }
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>

            {/* Modals */}
            <AddStaffModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleStaffAdded}
                bankId={bankId || undefined}
            />

            <DeactivationModal
                isOpen={showDeactivationModal}
                onClose={() => {
                    setShowDeactivationModal(false);
                    setPsoToDeactivate(null);
                }}
                onConfirm={handleDeactivate}
                psoName={psoToDeactivate?.name}
            />

            <Modal
                isOpen={showDeleteModal && Boolean(psoToDelete)}
                title="Remove staff permanently?"
                size="sm"
                onClose={() => {
                    if (deleteSubmitting) return;
                    setShowDeleteModal(false);
                    setPsoToDelete(null);
                }}
                closeOnBackdropClick={!deleteSubmitting}
            >
                <div className="space-y-4 p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        This removes <strong className="text-gray-900 dark:text-gray-100">{psoToDelete?.name ?? 'this person'}</strong> from{' '}
                        the bank directory and deletes their Firebase Auth account (when a login exists). This cannot be undone from the app.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={deleteSubmitting}
                            onClick={() => {
                                setShowDeleteModal(false);
                                setPsoToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button type="button" disabled={deleteSubmitting} className="bg-red-600 hover:bg-red-700" onClick={handleDeleteConfirm}>
                            {deleteSubmitting ? 'Removing…' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showAccessConfirmModal && Boolean(psoToAccess)}
                title="Access staff account?"
                size="sm"
                onClose={() => {
                    setShowAccessConfirmModal(false);
                    setPsoToAccess(null);
                }}
            >
                <div className="space-y-4 p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Are you sure you want to log in as <strong className="text-gray-900 dark:text-gray-100">{psoToAccess?.name ?? 'this user'}</strong>?
                        This will switch your session to this user.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                                setShowAccessConfirmModal(false);
                                setPsoToAccess(null);
                            }}
                        >
                            No
                        </Button>
                        <Button
                            type="button"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={handleAccessConfirm}
                        >
                            Yes
                        </Button>
                    </div>
                </div>
            </Modal>

            <PSODetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedPSO(null);
                }}
                pso={selectedPSO}
                bankId={bankId || undefined}
                onUpdate={async (updatedData: Partial<PSO>) => {
                    if (!selectedPSO || !bankId) return;
                    try {
                        // If email changed and this PSO has an Auth account, update Firebase Auth email first.
                        const nextEmail = (updatedData.email ?? '').toString().trim();
                        const prevEmail = (selectedPSO.email ?? '').toString().trim();
                        if (nextEmail && nextEmail !== prevEmail) {
                            if (!selectedPSO.authUid) {
                                throw new Error('This user is not registered in Auth (no authUid). Register login first, then update email.');
                            }
                            await updateUserEmail(selectedPSO.authUid, nextEmail);
                        }

                        await updatePSO(bankId, selectedPSO.id, updatedData);
                        const psosData = await fetchPSOs(bankId);
                        setPsos(psosData);

                        // Extract unique designations
                        const designationSet = new Set<string>();
                        psosData.forEach(pso => {
                            if (pso.designation) {
                                designationSet.add(pso.designation);
                            }
                        });
                        setDesignations(Array.from(designationSet));

                        // Filtered list will be updated by useEffect
                        showToast('✅ PSO updated successfully!', 'success');
                        setShowDetailsModal(false);
                    } catch (err: any) {
                        throw err;
                    }
                }}
            />

            {/* Toast */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </>
    );
}

